/* eslint-disable perfectionist/sort-exports, perfectionist/sort-named-exports, perfectionist/sort-modules */
import capitalMarketsHttp from '~/services/capital-markets/capital-markets.http';

export type CessionStatus =
  | 'awaiting_black_endorsement_generation'
  | 'awaiting_return_cession_intermediator'
  | 'cancelled_by_user'
  | 'cession_cancelled_by_intermediator'
  | 'changed_securitization'
  | 'done'
  | 'endorsement_black_sent_for_signature'
  | 'failed_import_financing'
  | 'failed_import_installments'
  | 'failed_intermediator'
  | 'generating_endorsement_black'
  | 'in_creation'
  | 'in_progress'
  | 'invalidated_by_intermediator'
  | 'partially_done'
  | 'pending_send_intermediator'
  | 'sending_backing'
  | 'sending_cession_intermediator'
  | 'sync_installments'
  | 'synced_installments';

export type CessionStatusEnum =
  | 'awaiting_black_endorsement_generation'
  | 'awaiting_return_cession_intermediator'
  | 'cancelled_by_user'
  | 'cession_cancelled_by_intermediator'
  | 'changed_securitization'
  | 'done'
  | 'endorsement_black_sent_for_signature'
  | 'failed_import_financing'
  | 'failed_import_installments'
  | 'failed_intermediator'
  | 'generating_endorsement_black'
  | 'in_creation'
  | 'in_progress'
  | 'invalidated_by_intermediator'
  | 'partially_done'
  | 'pending_send_intermediator'
  | 'sending_backing'
  | 'sending_cession_intermediator'
  | 'sync_installments'
  | 'synced_installments';

export type CessionFinancingStatusEnum =
  | 'awaiting_black_endorsement_generation'
  | 'awaiting_endorsement_signatures'
  | 'awaiting_return_cession_intermediator'
  | 'backing_sent'
  | 'cancelled_by_user'
  | 'cession_cancelled_by_intermediator'
  | 'done'
  | 'failed_backing_not_found'
  | 'failed_send_backing'
  | 'failure_import_installments'
  | 'failure_send_cession_intermediator'
  | 'generating_endorsement_black'
  | 'in_progress'
  | 'invalidated_by_intermediator'
  | 'pending_send_intermediator'
  | 'sending_backing'
  | 'sending_cession_intermediator'
  | 'signed'
  | 'synced_installments'
  | 'waiting_import_installments';

export type CessionCategoryEnum = 'new_cession' | 'overturning' | 'renegotiation';

export type CessionLogTypeEnum = 'error' | 'info' | 'warning';

export type PaymentStatusEnum = 'pending' | 'completed' | 'not_applicable' | 'failed';

export type CessionTypeEnum = 'cancellation' | 'creation';

export type OperationType = 'new_cession' | 'overturning' | 'renegotiation';

export type SearchType = 'ccb' | 'cession_id' | 'financing_id';

export type SortBy = 'created_at' | 'updated_at';

export type SortDirection = 'asc' | 'desc';

export interface BankingProviderValues {
  acquisition_amount: number;
  financed_amount: number;
  gross_amount: number;
}

export interface Cession {
  acquisition_amount: number;
  agio: number;
  canceled_at: null | string;
  canceled_by: null | string;
  ceded_at: null | string;
  cession_id: number;
  cut_off_date: string;
  endorsement_uploaded_at: null | string;
  endorsement_uri: null | string;
  financed_amount: number;
  gross_amount: number;
  operation_date: string;
  operation_type: OperationType;
  securitization_name: string;
  sent_at: null | string;
  status: CessionStatus;
  total_financings: number;
  type: string;
  updated_at: string;
}

export interface CessionLogItem {
  created_at: string;
  created_by: null | string;
  description: string;
  id: number;
  type: CessionLogTypeEnum;
}

export interface FinancingItem {
  acquisition_amount: number;
  ccb: string;
  cession_sent_at: null | string;
  customer_name: string;
  financed_amount: number;
  formalization_date: string;
  gross_amount: number;
  id: number;
  installments_number: number;
  integration_status: CessionFinancingStatusEnum;
  intermediator_external_id: null | string;
  payment_status: PaymentStatusEnum;
  project_value: number;
  rate_type: string;
  securitization_origin: string;
  solfacil_financing_id: number;
}

export interface PaginationMetadata {
  current_page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

export interface FinancingsPaginated {
  items: FinancingItem[];
  metadata: PaginationMetadata;
}

export interface CessionActions {
  cancel: boolean;
  endorsement: boolean;
  finalize: boolean;
  forward: boolean;
  send_backing: boolean;
  sync_installments: boolean;
}

export interface CessionDetail {
  actions: CessionActions;
  aggregated_financing_values: Record<string, BankingProviderValues>;
  category: CessionCategoryEnum;
  ceded_at: string;
  financings: FinancingsPaginated;
  id: number;
  irr: null | number;
  logs: CessionLogItem[];
  operation_date: string;
  premium: null | number;
  securitization_name: string;
  status: CessionStatusEnum;
  total_acquisition_amount: number;
  total_financed_amount: number;
  total_gross_amount: number;
  type: CessionTypeEnum;
}

export interface GetCessionsRequest {
  end_date?: string;
  operation_types?: OperationType | OperationType[];
  page?: number;
  page_size?: number;
  search_query?: string;
  search_type?: SearchType;
  sort_by?: SortBy;
  sort_direction?: SortDirection;
  start_date?: string;
  status_param?: CessionStatus;
}

export interface GetCessionsResponse {
  current_page: number;
  has_next: boolean;
  items: Cession[];
  page_size: number;
  total_count: number;
  total_pages: number;
}

export interface FinalizeCessionResponse {
  cession_id: number;
  is_cession_finalized: boolean;
  message: string;
  results: {
    invalid_status: number[];
    not_found: number[];
    skipped: number[];
    success: number[];
  };
}

export interface SyncInstallmentsResponse {
  ceded_at: string;
  cut_off_date: string;
  id: number;
  status: string;
}

export interface ProcessCessionRequest {
  agio: number;
  ceded_at: string;
}

export interface ProcessCessionResponse {
  agio: number;
  ceded_at: string;
  cut_off_date: string;
  id: number;
  message: string;
  status: string;
}

export interface CreateCessionPayload {
  category: CessionCategoryEnum;
  ceded_at: string;
  premium: number;
  securitization_name: string;
  solfacil_financing_ids: number[];
  user: string;
}

export interface CreateCessionResponseItem {
  created_at: string;
  id: number;
  message: string;
  securitization_id: number;
  status: string;
  total_financings: number;
}

export type CreateCessionResponse = CreateCessionResponseItem[];

export interface Financing {
  ccb: string;
  client_name: string;
  external_slug: string;
  formalization_date: string;
  solfacil_financing_id: number;
  status: string;
  type: string;
}

export interface FilterFinancingsRequest {
  end_date?: string;
  financing_ids?: number[];
  start_date?: string;
  status?: string[];
}

export interface FilterFinancingsResponse {
  items: Financing[];
  page: number;
  pages: number;
  size: number;
  total: number;
}

export interface ImportFinancingsRequest {
  solfacil_financing_ids: number[];
}

export interface ImportFinancingsResponse {
  failed: number[];
  imported: number[];
}

export interface FinancingDetailRequest {
  financing_id: number;
}

export interface FinancingDetailResponse {
  [key: string]: any;
  id: number;
}

export interface Securitization {
  id: number;
  name: string;
}

export interface GetSecuritizationsResponse {
  items: Securitization[];
  page: number;
  pages: number;
  size: number;
  total: number;
}

export interface AddFinancingsToCessionRequest {
  solfacil_financing_ids: number[];
}

export interface RemoveFinancingsFromCessionRequest {
  solfacil_financing_ids: number[];
}

export interface FinancingOperationResult {
  financing_id: number;
  message: string;
  solfacil_financing_id: number;
  success: boolean;
}

export interface FinancingOperationResponse {
  cession_id: number;
  message: string;
  results: FinancingOperationResult[];
  success: boolean;
}

export interface AutomaticCessionConfig {
  enabled: boolean;
  premium: number;
}

export interface ConfigurationResponse {
  automatic_cession: AutomaticCessionConfig;
}

export interface UpdateConfigurationRequest {
  automatic_cession: AutomaticCessionConfig;
}

export interface BlocklistItem {
  ccb: string;
  created_at: string;
  created_by: string;
  financing_id: number;
  id: number;
  reason: string;
  solfacil_financing_id: number;
}

export interface GetBlocklistRequest {
  ccb?: string;
  page?: number;
  page_size?: number;
  sort_direction?: SortDirection;
}

export interface GetBlocklistResponse {
  current_page: number;
  has_next: boolean;
  items: BlocklistItem[];
  page_size: number;
  total_count: number;
  total_pages: number;
}

export interface AddToBlocklistRequest {
  ccb: string;
  reason: string;
}

export interface AddToBlocklistResponse {
  ccb: string;
  id: number;
  message: string;
}

export interface RemoveFromBlocklistResponse {
  ccb: string;
  financing_id: number;
  message: string;
}

class CapitalMarketsService {
  private readonly endpoint = '/cession';

  private static buildQueryString(params: GetCessionsRequest): string {
    const searchParams = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null && item !== '') {
              searchParams.append(key, item.toString());
            }
          });
        } else {
          searchParams.append(key, value.toString());
        }
      }
    });

    return searchParams.toString();
  }

  // eslint-disable-next-line class-methods-use-this
  async addFinancingsToCession(
    cessionId: number,
    solfacilFinancingIds: number[],
  ): Promise<FinancingOperationResponse> {
    const response = await capitalMarketsHttp.post<FinancingOperationResponse>(
      `/cession/${cessionId}/financing`,
      { solfacil_financing_ids: solfacilFinancingIds },
    );
    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async addToBlocklist(payload: AddToBlocklistRequest): Promise<AddToBlocklistResponse> {
    const response = await capitalMarketsHttp.post<AddToBlocklistResponse>('/financing/blocklist', payload);
    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async createCession(payload: CreateCessionPayload[]): Promise<CreateCessionResponse> {
    const response = await capitalMarketsHttp.post<CreateCessionResponse>('/v2/cession', payload);
    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async downloadCessionCsv(cessionId: number): Promise<{ data: Blob; filename: string }> {
    const response = await capitalMarketsHttp.get(`/cession/${cessionId}/report/csv`, {
      responseType: 'blob',
    });

    const contentDisposition = response.headers['content-disposition'];
    let filename = `relatorio_cessao_${cessionId}.csv`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/"/g, '');
      }
    }

    return {
      data: response.data as Blob,
      filename,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  async downloadContractFile(solfacilFinancingId: number, fileUri: string): Promise<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.append('solfacil_financing_id', solfacilFinancingId.toString());
    queryParams.append('file_uri', fileUri);
    const url = `/financing/contract-files/download?${queryParams.toString()}`;
    const response = await capitalMarketsHttp.get<{ base64: string }>(url);

    // Decode base64 string to binary
    const base64Data = response.data.base64;
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create blob from decoded bytes
    return new Blob([bytes], { type: 'application/pdf' });
  }

  // eslint-disable-next-line class-methods-use-this
  async filterFinancings(
    params: FilterFinancingsRequest,
    page: number = 1,
    size: number = 10,
  ): Promise<FilterFinancingsResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('size', size.toString());
    const url = `/financing/filter?${queryParams.toString()}`;
    const response = await capitalMarketsHttp.post<FilterFinancingsResponse>(url, params);
    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async finalize(cessionId: number, solfacilFinancingIds: number[]): Promise<FinalizeCessionResponse> {
    const response = await capitalMarketsHttp.post<FinalizeCessionResponse>(`/cession/${cessionId}/finalize`, {
      solfacil_financing_ids: solfacilFinancingIds,
    });
    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async getBlocklist(params?: GetBlocklistRequest): Promise<GetBlocklistResponse> {
    const queryParams = new URLSearchParams();

    if (params?.ccb) {
      queryParams.append('ccb', params.ccb);
    }
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.page_size) {
      queryParams.append('page_size', params.page_size.toString());
    }
    if (params?.sort_direction) {
      queryParams.append('sort_direction', params.sort_direction);
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/financing/blocklist?${queryString}` : '/financing/blocklist';
    const response = await capitalMarketsHttp.get<GetBlocklistResponse>(url);
    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async getConfiguration(): Promise<ConfigurationResponse> {
    const response = await capitalMarketsHttp.get<ConfigurationResponse>('/configuration');
    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async getDetail(
    cessionId: number,
    params?: {
      integration_status?: string;
      page?: number;
      page_size?: number;
      search_query?: string;
      search_type?: string;
    },
  ): Promise<CessionDetail> {
    const queryParams = new URLSearchParams();
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.page_size) {
      queryParams.append('page_size', params.page_size.toString());
    }
    if (params?.integration_status) {
      queryParams.append('integration_status', params.integration_status);
    }
    if (params?.search_query) {
      queryParams.append('search_query', params.search_query);
    }
    if (params?.search_type) {
      queryParams.append('search_type', params.search_type);
    }
    const queryString = queryParams.toString();
    const baseUrl = `/cession/${cessionId}/detail`;
    const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;
    const response = await capitalMarketsHttp.get<CessionDetail>(url);

    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async getFinancingDetail(payload: FinancingDetailRequest): Promise<FinancingDetailResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('solfacil_financing_id', payload.financing_id.toString());
    const url = `/financing/detail?${queryParams.toString()}`;
    const response = await capitalMarketsHttp.get<FinancingDetailResponse>(url);
    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async getSecuritizations(): Promise<GetSecuritizationsResponse> {
    const response = await capitalMarketsHttp.get<GetSecuritizationsResponse>('/securitization/');
    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async importFinancings(payload: ImportFinancingsRequest): Promise<ImportFinancingsResponse> {
    const response = await capitalMarketsHttp.post<ImportFinancingsResponse>('/financing/import', payload);
    return response.data;
  }

  async list(params: GetCessionsRequest): Promise<GetCessionsResponse> {
    const queryString = CapitalMarketsService.buildQueryString(params);
    const url = queryString ? `${this.endpoint}?${queryString}` : this.endpoint;
    const response = await capitalMarketsHttp.get<GetCessionsResponse>(url);
    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async processCession(cessionId: number, payload: ProcessCessionRequest): Promise<ProcessCessionResponse> {
    const response = await capitalMarketsHttp.post<ProcessCessionResponse>(
      `/cession/${cessionId}/process`,
      payload,
    );
    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async removeFinancingsFromCession(
    cessionId: number,
    solfacilFinancingIds: number[],
  ): Promise<FinancingOperationResponse> {
    const response = await capitalMarketsHttp.post<FinancingOperationResponse>(
      `/cession/${cessionId}/financing/unbind`,
      { solfacil_financing_ids: solfacilFinancingIds },
    );
    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async removeFromBlocklist(ccb: string): Promise<RemoveFromBlocklistResponse> {
    const response = await capitalMarketsHttp.delete<RemoveFromBlocklistResponse>(`/financing/blocklist/${ccb}`);
    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async syncInstallments(cessionId: number): Promise<SyncInstallmentsResponse> {
    const response = await capitalMarketsHttp.post<
      SyncInstallmentsResponse
    >(`/cession/${cessionId}/sync-installments`);

    return response.data;
  }

  // eslint-disable-next-line class-methods-use-this
  async updateConfiguration(data: UpdateConfigurationRequest): Promise<ConfigurationResponse> {
    const response = await capitalMarketsHttp.put<ConfigurationResponse>('/configuration', data);
    return response.data;
  }
}

const capitalMarketsService = new CapitalMarketsService();

export default capitalMarketsService;

export type CapitalMarketsOperation = Cession;
export type GetCapitalMarketsOperationsRequest = GetCessionsRequest;
export type GetCapitalMarketsOperationsResponse = GetCessionsResponse;
